import { useMemo } from 'react';
import { Typography } from 'antd';
import { parseFlowObject, parseFlowYaml } from '@/flow-ir/parse';
import { toRenderGraph } from '@/flow-ir/toReactFlow';
import { DesignerCanvas } from '@/designer/DesignerCanvas';
import { useAppSelector } from '@/store/hooks';

const { Text } = Typography;

/**
 * Read-only mini-canvas preview of an IR form — a YAML authoring string (the migration-review
 * drawer) or the canonical IR as an already-parsed JSON OBJECT (the template gallery's
 * TemplateDetail.irPreview wire shape) — with a structured JSON/text fallback when it does not
 * parse. Reuses the PS-2 designer canvas in non-editable mode. Guards every step — a malformed
 * preview never crashes the host.
 */
export function IrPreview({
  irPreview,
  height = 280,
}: {
  irPreview?: string | Record<string, unknown> | null;
  height?: number;
}) {
  const theme = useAppSelector((s) => s.ui.theme);

  const parsed = useMemo(() => {
    if (!irPreview) return null;
    try {
      return typeof irPreview === 'string' ? parseFlowYaml(irPreview) : parseFlowObject(irPreview);
    } catch {
      return null;
    }
  }, [irPreview]);

  const graph = useMemo(
    () => (parsed ? toRenderGraph(parsed, { dataLayerVisible: false, editable: false }) : null),
    [parsed],
  );

  const fallbackText = useMemo(() => {
    if (!irPreview) return '';
    if (typeof irPreview === 'string') return irPreview;
    try {
      return JSON.stringify(irPreview, null, 2);
    } catch {
      return String(irPreview);
    }
  }, [irPreview]);

  if (!irPreview) return <Text type="secondary">No IR preview available.</Text>;

  if (!graph) {
    return (
      <pre
        className="ol-mono"
        style={{
          fontSize: 11,
          background: 'var(--ol-bg-base)',
          border: '1px solid var(--ol-border)',
          borderRadius: 6,
          padding: 10,
          maxHeight: height,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
        }}
      >
        {fallbackText}
      </pre>
    );
  }

  return (
    <div style={{ height, border: '1px solid var(--ol-border)', borderRadius: 8, overflow: 'hidden' }}>
      <DesignerCanvas
        nodes={graph.nodes}
        edges={graph.edges}
        layout={graph.layout}
        colorMode={theme}
        editable={false}
        placing={false}
        onSelectNode={() => {}}
        onSelectEdge={() => {}}
      />
    </div>
  );
}
